import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Ministry } from '../../../models/ministry/ministry';
import {ROUTES} from "../../../constants/routes";

interface MinistryCardProps {
  ministry: Ministry;
  className?: string;
}

const MinistryCard: React.FC<MinistryCardProps> = ({ ministry, className = '' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [imageLoading, setImageLoading] = React.useState(true);
  const [imageError, setImageError] = React.useState(false);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <div className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group hover:scale-105 flex flex-col h-full ${className}`}>
      {/* Ministry Image/Icon */}
      <div className="relative h-64 overflow-hidden bg-white">
        {/* Loading Placeholder */}
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-3">
              <svg className="w-8 h-8 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div className="text-xs text-gray-500">{t('ui.loading', 'Loading...')}</div>
            </div>
          </div>
        )}

        {/* Error Placeholder */}
        {imageError && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-2 text-gray-400">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="text-xs">Image unavailable</div>
            </div>
          </div>
        )}

        {ministry.media.cover?.url ? (
          // Check if cover is an SVG logo - if so, center it like a logo
          ministry.media.cover.url.endsWith('.svg') ? (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={ministry.media.cover.url}
                alt={ministry.title}
                className={`w-40 h-40 object-contain opacity-90 group-hover:scale-150 transition-all duration-700 ${imageLoading ? 'opacity-0' : 'opacity-90'}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </div>
          ) : (
            <img
              src={ministry.media.cover.url}
              alt={ministry.title}
              className={`w-full h-full scale-100 object-cover group-hover:scale-175 transition-all duration-700 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {ministry.media.logo?.url ? (
              <img
                src={ministry.media.logo.url}
                alt={ministry.title}
                className={`w-40 h-40 object-contain opacity-60 group-hover:scale-175 transition-all duration-700 ${imageLoading ? 'opacity-0' : 'opacity-60'}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            ) : (
              <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            )}
          </div>
        )}

        {/* Ministry Category Badge */}
        {/*<div className="absolute top-4 left-4 bg-black/80 text-white px-3 py-1 rounded-full text-sm font-semibold backdrop-blur-sm">*/}
        {/*  {ministry.category}*/}
        {/*</div>*/}
      </div>

      {/* Ministry Content */}
      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-black transition-colors">
          {ministry.title}
        </h3>

        <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
          {ministry.description}
        </p>

        {/* Ministry Details */}
        <div className="space-y-2 mb-6 flex-grow">
          {ministry.duration && (
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-sm text-gray-500`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{ministry.duration}</span>
            </div>
          )}

          {ministry.address && (
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-sm text-gray-500`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{ministry.address.name}</span>
            </div>
          )}

          {ministry.time && (
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-sm text-gray-500`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="truncate">{ministry.time}</span>
            </div>
          )}
        </div>

        {/* Action Button - Always at bottom */}
        <div className="mt-auto">
          <Link
              to={`${ROUTES.MINISTRIES}/${ministry.id}`}
            className="inline-flex items-center justify-center w-full bg-black text-white hover:bg-gray-800 py-3 px-6 rounded-lg transition-all duration-300 font-semibold hover:scale-105"
          >
            <span>{t('pages.home.ministries.learnMoreJoin')}</span>
            <svg className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MinistryCard;
